import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Plus, Minus, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { apiRequest, formatPrice } from '../lib/utils';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const COMBO_CATEGORIES = [
  'beb8bf7c-2d05-4121-ba20-3274d54ff944',
  'cat-sandos',
  '293aafc0-e532-4e4b-ac4c-9d34dc7e66c9',
];
const COMBO_PRICE = 6.0;
const PIZZA_CATEGORY = 'cat-pizza';
const ETHNIC_CATEGORY = 'cat-ethnic';

const ItemDetailPage = () => {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { addToCart } = useCart();

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [removedIngredients, setRemovedIngredients] = useState([]);
  const [notes, setNotes] = useState('');
  const [hasCombo, setHasCombo] = useState(false);
  const [selectedSize, setSelectedSize] = useState('normal');
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [availableAddons, setAvailableAddons] = useState([]);

  useEffect(() => {
    const fetchItem = async () => {
      try {
        const data = await apiRequest(`/menu/${itemId}`);
        setItem(data);
        if (data?.category_id) {
          const addons = await apiRequest(`/addons?category_id=${data.category_id}`);
          setAvailableAddons(addons);
        }
      } catch (error) {
        console.error('Error fetching item:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchItem();
  }, [itemId]);

  // Set document title and canonical for SEO
  useEffect(() => {
    if (item) {
      document.title = item.name + ' | Tasty Bites - Halal Food Takeaway Lugano';
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', 'https://www.tastybiteslugano.ch/menu/item/' + itemId);
    }
    return () => {
      document.title = 'Tasty Bites - Halal Food Takeaway Lugano';
    };
  }, [item, itemId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse space-y-4 w-full max-w-2xl mx-auto px-4">
          <div className="h-64 bg-[#1C1C22] rounded-xl" />
          <div className="h-6 bg-[#1C1C22] rounded w-1/2" />
          <div className="h-4 bg-[#1C1C22] rounded w-3/4" />
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-zinc-400 text-lg">Item not found</p>
        <Button onClick={() => navigate('/menu')} className="bg-[#FFC107] text-black hover:bg-[#F59E0B]">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Menu
        </Button>
      </div>
    );
  }

  // Generate title string safely - only after item is loaded
  // NOTE: Helmet removed due to react-helmet-async bug with dynamic titles
  // const pageTitle = `${item.name} | Tasty Bites - Halal Food Takeaway Lugano`;
  // const pageDescription = `${item.name} - ${item.description || 'Order from Tasty Bites Lugano. Fresh halal food, delivery & pickup available.'}`;

  const ingredients = item.ingredients || [];
  const isComboEligible = COMBO_CATEGORIES.includes(item.category_id);
  const isPizza = item.category_id === PIZZA_CATEGORY;
  const isEthnic = item.category_id === ETHNIC_CATEGORY;
  const hasBigPrice = isPizza && item.price_big != null;

  const getTranslatedIngredients = () => {
    const lang = i18n.language;
    if (lang === 'it' && item.ingredients_it?.length === ingredients.length) return item.ingredients_it;
    if (lang === 'de' && item.ingredients_de?.length === ingredients.length) return item.ingredients_de;
    if (lang === 'fr' && item.ingredients_fr?.length === ingredients.length) return item.ingredients_fr;
    if (lang === 'ar' && item.ingredients_ar?.length === ingredients.length) return item.ingredients_ar;
    return ingredients;
  };
  const translatedIngredients = getTranslatedIngredients();

  const getTranslatedRemovedNames = () => {
    return removedIngredients.map(eng => {
      const idx = ingredients.indexOf(eng);
      return idx >= 0 && translatedIngredients[idx] ? translatedIngredients[idx] : eng;
    });
  };

  const toggleIngredient = (ingredient) => {
    setRemovedIngredients(prev =>
      prev.includes(ingredient) ? prev.filter(i => i !== ingredient) : [...prev, ingredient]
    );
  };

  const toggleAddon = (addon) => {
    setSelectedAddons(prev => {
      const exists = prev.find(a => a.addon_id === addon.id);
      if (exists) return prev.filter(a => a.addon_id !== addon.id);
      return [...prev, { addon_id: addon.id, name: addon.name, price: addon.price }];
    });
  };

  const getLocalizedAddonName = (addon) => {
    const lang = i18n.language;
    if (lang === 'it' && addon.name_it) return addon.name_it;
    if (lang === 'de' && addon.name_de) return addon.name_de;
    if (lang === 'fr' && addon.name_fr) return addon.name_fr;
    if (lang === 'ar' && addon.name_ar) return addon.name_ar;
    return addon.name;
  };

  const getDescription = () => {
    const lang = i18n.language;
    if (lang === 'it' && item.description_it) return item.description_it;
    if (lang === 'de' && item.description_de) return item.description_de;
    if (lang === 'fr' && item.description_fr) return item.description_fr;
    if (lang === 'ar' && item.description_ar) return item.description_ar;
    return item.description;
  };

  // Item names always shown in English (as created)
  const getLocalizedName = () => item.name;

  const addonsTotal = selectedAddons.reduce((sum, a) => sum + a.price, 0);
  const basePrice = (hasBigPrice && selectedSize === 'big') ? item.price_big : item.price;
  const itemPrice = basePrice + (hasCombo ? COMBO_PRICE : 0) + addonsTotal;

  const handleAdd = async () => {
    try {
      await addToCart(
        item.id, quantity, notes || '', removedIngredients,
        hasCombo, hasBigPrice ? selectedSize : null, selectedAddons
      );
      toast.success(t('menu.addedToCart', { item: item.name }));
      navigate(-1);
    } catch (error) {
      toast.error(t('menu.addToCartError'));
    }
  };

  return (
    <div className="min-h-screen pb-28" data-testid="item-detail-page">
      {/* Back Button */}
      <div className="max-w-3xl mx-auto px-4 pt-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-4"
          data-testid="item-detail-back"
        >
          <ArrowLeft className="h-5 w-5" />
          <span className="text-sm font-medium">{t('menu.title')}</span>
        </button>
      </div>

      {/* Hero Image */}
      {item.image && (
        <div className="max-w-3xl mx-auto px-4 mb-6">
          <div className="relative w-full aspect-[16/9] sm:aspect-[2/1] rounded-2xl overflow-hidden">
            <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/60 via-transparent to-transparent" />
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto px-4">
        {/* Name & Price */}
        <div className="flex items-start justify-between mb-3">
          <h1 className="text-3xl sm:text-4xl font-bold text-white font-['Barlow_Condensed'] uppercase" data-testid="item-detail-name">
            {getLocalizedName()}
          </h1>
          {!hasBigPrice && (
            <span className="text-2xl sm:text-3xl font-bold text-[#FFC107] ml-4 flex-shrink-0">
              {formatPrice(item.price)}
            </span>
          )}
        </div>

        {/* Description */}
        {getDescription() && (
          <p className="text-base text-zinc-400 mb-6 leading-relaxed">{getDescription()}</p>
        )}

        {/* Size Selector (Pizza only) */}
        {hasBigPrice && (
          <div className="mb-6" data-testid="size-selector">
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setSelectedSize('normal')}
                className={`py-4 px-5 rounded-xl border-2 transition-all text-center ${
                  selectedSize === 'normal'
                    ? 'border-[#FFC107] bg-[#FFC107]/15'
                    : 'border-zinc-700 bg-[#1C1C22]/60 hover:border-zinc-600'
                }`}
                data-testid="size-normal"
              >
                <span className={`text-sm font-semibold block ${selectedSize === 'normal' ? 'text-[#FFC107]' : 'text-white'}`}>
                  {t('itemDetail.sizeNormal')}
                </span>
                <span className={`text-xl font-bold block mt-1 ${selectedSize === 'normal' ? 'text-[#FFC107]' : 'text-zinc-400'}`}>
                  CHF {item.price?.toFixed(2)}
                </span>
              </button>
              <button
                onClick={() => setSelectedSize('big')}
                className={`py-4 px-5 rounded-xl border-2 transition-all text-center ${
                  selectedSize === 'big'
                    ? 'border-[#FFC107] bg-[#FFC107]/15'
                    : 'border-zinc-700 bg-[#1C1C22]/60 hover:border-zinc-600'
                }`}
                data-testid="size-big"
              >
                <span className={`text-sm font-semibold block ${selectedSize === 'big' ? 'text-[#FFC107]' : 'text-white'}`}>
                  {t('itemDetail.sizeBig')}
                </span>
                <span className={`text-xl font-bold block mt-1 ${selectedSize === 'big' ? 'text-[#FFC107]' : 'text-zinc-400'}`}>
                  CHF {item.price_big?.toFixed(2)}
                </span>
              </button>
            </div>
          </div>
        )}

        {/* Ingredients */}
        {ingredients.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-3">
              {t('itemDetail.ingredients')}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {ingredients.map((ingredient, idx) => {
                const displayName = translatedIngredients[idx] || ingredient;
                if (isEthnic) {
                  return (
                    <div
                      key={ingredient}
                      className="flex items-center gap-3 px-4 py-3 rounded-lg bg-[#1C1C22]/60 border border-zinc-700/50"
                      data-testid={`ingredient-${ingredient.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <div className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 bg-[#FFC107]/20 border border-[#FFC107]/50">
                        <Check className="h-3 w-3 text-[#FFC107]" />
                      </div>
                      <span className="text-sm text-white">{displayName}</span>
                    </div>
                  );
                }
                const isRemoved = removedIngredients.includes(ingredient);
                return (
                  <button
                    key={ingredient}
                    onClick={() => toggleIngredient(ingredient)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-left ${
                      isRemoved
                        ? 'bg-red-500/10 border border-red-500/30'
                        : 'bg-[#1C1C22]/60 border border-zinc-700/50 hover:border-zinc-600'
                    }`}
                    data-testid={`ingredient-${ingredient.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                      isRemoved ? 'bg-red-500/20 border border-red-500' : 'bg-[#FFC107]/20 border border-[#FFC107]/50'
                    }`}>
                      {!isRemoved && <Check className="h-3 w-3 text-[#FFC107]" />}
                    </div>
                    <span className={`text-sm ${isRemoved ? 'text-red-400 line-through' : 'text-white'}`}>
                      {displayName}
                    </span>
                  </button>
                );
              })}
            </div>
            {!isEthnic && removedIngredients.length > 0 && (
              <p className="text-xs text-red-400 mt-2">
                {t('itemDetail.withoutItems', { items: getTranslatedRemovedNames().join(', ') })}
              </p>
            )}
          </div>
        )}

        {/* Add-ons/Extras */}
        {availableAddons.length > 0 && (
          <div className="mb-6" data-testid="addons-section">
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-3">
              {t('itemDetail.extras')}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {availableAddons.map((addon) => {
                const isSelected = selectedAddons.some(a => a.addon_id === addon.id);
                return (
                  <button
                    key={addon.id}
                    onClick={() => toggleAddon(addon)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-left ${
                      isSelected
                        ? 'bg-[#FFC107]/15 border border-[#FFC107]/50'
                        : 'bg-[#1C1C22]/60 border border-zinc-700/50 hover:border-zinc-600'
                    }`}
                    data-testid={`addon-${addon.id}`}
                  >
                    <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                      isSelected ? 'bg-[#FFC107] border border-[#FFC107]' : 'bg-zinc-700 border border-zinc-600'
                    }`}>
                      {isSelected && <Check className="h-3 w-3 text-black" />}
                    </div>
                    <span className={`text-sm flex-1 ${isSelected ? 'text-[#FFC107]' : 'text-white'}`}>
                      {getLocalizedAddonName(addon)}
                    </span>
                    <span className={`text-sm font-bold ${isSelected ? 'text-[#FFC107]' : 'text-zinc-400'}`}>
                      +CHF {addon.price.toFixed(2)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Allergy Warning */}
        <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl" data-testid="allergy-warning">
          <p className="text-xs text-amber-400 font-medium">{t('itemDetail.allergyWarning')}</p>
          <a href="tel:0919231428" className="text-xs text-amber-300 mt-1 font-semibold hover:underline">{t('itemDetail.contactUs')}</a>
          <p className="text-xs text-zinc-400 mt-1">{t('menu.imageDisclaimer')}</p>
        </div>

        {/* Combo Option */}
        {isComboEligible && (
          <button
            onClick={() => setHasCombo(!hasCombo)}
            className={`w-full mb-6 flex items-center gap-3 px-5 py-4 rounded-xl transition-all text-left ${
              hasCombo
                ? 'bg-[#FFC107]/15 border-2 border-[#FFC107]'
                : 'bg-[#1C1C22]/60 border-2 border-zinc-700/50 hover:border-zinc-600'
            }`}
            data-testid="combo-toggle"
          >
            <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${
              hasCombo ? 'bg-[#FFC107] border border-[#FFC107]' : 'bg-zinc-700 border border-zinc-600'
            }`}>
              {hasCombo && <Check className="h-4 w-4 text-black" />}
            </div>
            <div className="flex-1">
              <span className={`text-sm font-semibold ${hasCombo ? 'text-[#FFC107]' : 'text-white'}`}>
                {t('itemDetail.comboTitle')}
              </span>
              <p className="text-xs text-zinc-400 mt-0.5">{t('itemDetail.comboDesc')}</p>
            </div>
            <span className={`text-sm font-bold ${hasCombo ? 'text-[#FFC107]' : 'text-zinc-400'}`}>
              +CHF 6.00
            </span>
          </button>
        )}

        {/* Special Instructions */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-2">
            {t('itemDetail.specialNotes')}
          </h3>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('itemDetail.notesPlaceholder')}
            className="w-full bg-[#1C1C22]/60 border border-zinc-700/50 rounded-xl px-4 py-3 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-[#FFC107]/50 resize-none h-20"
            data-testid="item-detail-notes"
          />
        </div>
      </div>

      {/* Fixed Bottom Bar - Quantity & Add to Cart */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#141417]/95 backdrop-blur-md border-t border-[#27272A]">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-4">
          <div className="flex items-center bg-[#1C1C22] rounded-lg">
            <button
              onClick={() => setQuantity(q => Math.max(1, q - 1))}
              className="w-11 h-11 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
              data-testid="item-detail-qty-minus"
            >
              <Minus className="h-4 w-4" />
            </button>
            <span className="w-8 text-center font-bold text-white" data-testid="item-detail-qty">{quantity}</span>
            <button
              onClick={() => setQuantity(q => q + 1)}
              className="w-11 h-11 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
              data-testid="item-detail-qty-plus"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
          <Button
            onClick={handleAdd}
            className="flex-1 h-12 bg-[#FFC107] text-black hover:bg-[#F59E0B] font-bold uppercase tracking-wide text-base"
            data-testid="item-detail-add-btn"
          >
            {t('itemDetail.addToCart')} — CHF {(itemPrice * quantity).toFixed(2)}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ItemDetailPage;
